<template>

    <div class="row h-100">
        <div class="col-sm-10 col-md-11 col-lg-8 my-auto mx-auto">
            <div class="row">
                <div class="col-md-6 bg-white py-5 px-5">
                    <div class="row">
                        <div class="col-12 px-5 px-md-2">

                            <h1 class="text-center mb-3">
                                <span>Reset Password</span>
                            </h1>

                            <!-- Loading State -->
                            <div v-if="isValidating" class="text-center py-4">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <p class="mt-2 text-muted">Validating reset link...</p>
                            </div>

                            <!-- Invalid Token -->
                            <div v-else-if="tokenError" class="alert alert-danger text-center">
                                <h5>Invalid or Expired Link</h5>
                                <p class="mb-0">{{ tokenError }}</p>
                                <router-link to="/forgot-password" class="btn btn-outline-primary mt-3">Request New Link</router-link>
                            </div>

                            <!-- Success Message -->
                            <div v-else-if="resetSuccess" class="alert alert-success text-center">
                                <h5>Password Reset Successfully!</h5>
                                <p class="mb-0">Your password has been updated. You can now log in with your new password.</p>
                                <router-link to="/login" class="btn btn-outline-primary mt-3">Go to Login</router-link>
                            </div>

                            <!-- Reset Form -->
                            <template v-else-if="resetToken">
                                <p class="text-muted text-center mb-4">Enter your new password below.</p>

                                <kt-input
                                    class="my-2"
                                    v-model="newPassword"
                                    label="New Password"
                                    type="password"
                                    placeholder="Enter your new password"
                                    required
                                >
                                    <template #prefix>
                                        <div class="input-group-text">
                                        <i class="fas fa-lock"></i>
                                        </div>
                                    </template>
                            
                                </kt-input>

                                <kt-input
                                    class="my-2"
                                    v-model="confirmPassword"
                                    label="Confirm Password"
                                    type="password"
                                    placeholder="Confirm your new password"
                                    required
                                >
                                    <template #prefix>
                                        <div class="input-group-text">
                                        <i class="fas fa-lock"></i>
                                        </div>
                                    </template>
                            
                                </kt-input>

                                <div class="text-center">
                                    <button 
                                        class="btn btn-outline-primary mt-4 py-2 px-4 form-control" 
                                        @click="submitReset()"
                                        :disabled="isSubmitting"
                                    >
                                        <span v-if="isSubmitting">Resetting...</span>
                                        <span v-else>Reset Password</span>
                                    </button>
                                </div>

                                <div class="text-center my-2">
                                    <span class="text-muted"> &lt; Back to
                                        <router-link to="/login" class="text-muted">Login</router-link>
                                    </span>
                                </div>
                            </template>
                        </div>
                    
                    </div>
                </div>
                <div class="col-md-6 bg-login p-0">
                    <div class="bg h-100 d-flex py-3">
                        <div class="text-center my-auto text-white w-50 mx-auto">
                            <h4>
                                <img src="/favicon.svg" alt="7th Level Technologies" style="width: 25%; max-width: 100%; height: auto;" class="mb-3"><br />
                                Manage project time and resources with ease.
                            </h4>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<script setup>

import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Api from "@/services/ApiService";
import Alert from "@/services/Alert";
import KtInput from "@/components/UI/KtInput.vue"; 

const route = useRoute();
const router = useRouter();

const isValidating = ref(true);
const tokenError = ref("");
const resetToken = ref("");
const resetSuccess = ref(false);
const isSubmitting = ref(false);

const newPassword = ref("");
const confirmPassword = ref("");

// Validate the token on mount
onMounted(async () => {
    const token = route.params.token;
    
    if (!token) {
        tokenError.value = "No reset token provided.";
        isValidating.value = false;
        return;
    }

    try {
        const res = await Api.validateResetToken(token + "");
        
        console.debug("validate token response", res);

        // Http.ts returns data.Data directly on success, so res is { ResetToken: "..." }
        if (res && res.ResetToken) {
            resetToken.value = res.ResetToken;
        } else {
            tokenError.value = "This reset link is invalid or has expired.";
        }
    }
    catch (error) {
        console.error("Token validation error:", error);
        tokenError.value = "This reset link is invalid or has expired.";
    }
    finally {
        isValidating.value = false;
    }
});

const submitReset = async () => {
    // Validate passwords
    if (!newPassword.value || !confirmPassword.value) {
        Alert.notify({
            text: "Please enter and confirm your new password"
        });
        return;
    }

    if (newPassword.value.length < 6) {
        Alert.notify({
            text: "Password must be at least 6 characters long"
        });
        return;
    }

    if (newPassword.value !== confirmPassword.value) {
        Alert.notify({
            text: "Passwords do not match"
        });
        return;
    }

    isSubmitting.value = true;

    try {
        // Http.ts returns data.Data directly on success and shows Toast automatically
        // If we get here without an exception, the reset was successful
        await Api.resetPassword({
            resetToken: resetToken.value,
            newPassword: newPassword.value
        });
        
        resetSuccess.value = true;
    }
    catch (error) {
        console.error("Reset password error:", error);
        Alert.notify({
            text: "An error occurred. Please try again."
        });
    }
    finally {
        isSubmitting.value = false;
    }
}

</script>

<style>

#app.reset-password {
    background: #212121;
}

.bg-login {
    background-image: url("/images/login_bg.jpg");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    
}

.bg-login > .bg {
    background: linear-gradient(180deg,rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.74) 50%, rgba(0, 0, 0, 1) 100%);
}
</style>
